import { useState, useCallback } from "react";
import api from "../../../api/axios";
import { fetchCityStateForZip } from "../utils/apiUtils";
import ParcelSearchResultsModal from "../ParcelSearchResultsModal";

export const useSearchHandlers = (openModal, closeModal, onSwitchToAddressTab, onParcelSelected, onManualAddressResult, onUpdateQuery, mode = "seller") => {
  const [parcelState, setParcelState] = useState({
    parcelNumber: "",
    isSearching: false,
  });

  const [ownerState, setOwnerState] = useState({
    firstName: "",
    lastName: "",
    searchEntireState: false,
    isSearching: false,
  });

  const [addressState, setAddressState] = useState({
    isSearching: false,
  });

  const handleParcelSelect = useCallback((parcel) => {
    //console.log("handleParcelSelect 11", parcel);
    if (parcel) {
      // Parse address to extract street number and name
      const address = parcel.Address || "";
      const addressParts = address.trim().split(/\s+/);

      // Extract street number (first part if it's numeric)
      const street_no = addressParts.length > 0 && /^\d+$/.test(addressParts[0])
        ? addressParts[0]
        : "";

      // Extract street name (remaining parts)
      const street_name = addressParts.length > 1
        ? addressParts.slice(1).join(" ")
        : addressParts[0] || "";

      // Call the parcel selected callback with parsed data (similar to address autocomplete)
      if (onParcelSelected && typeof onParcelSelected === 'function') {
        const formattedAddress = address + " " + parcel.Unit + " " + parcel.City + " " + parcel.State + " " + parcel.Zip;
        const hasUnit = !!(parcel.Unit && parcel.Unit.trim());
        onParcelSelected({
          address: formattedAddress, // Full formatted address
          addressIncludesUnit: hasUnit, // Boolean indicating if address includes unit
          street_no: street_no,
          street_name: street_name,
          city: parcel.City || "",
          state: parcel.State || "",
          closing_state: parcel.State || "", // Also set closing_state for SetCountyModal
          zip: parcel.Zip || "",
          unitNumber: parcel.Unit || "",
          closing_county: parcel.city_name || "",
          formattedAddress: formattedAddress
        });

        // Update the search query with the formatted address
        if (onUpdateQuery && typeof onUpdateQuery === 'function') {
          onUpdateQuery(formattedAddress);
        }
      }

      // Switch to Address tab to show the populated fields
      if (onSwitchToAddressTab && typeof onSwitchToAddressTab === 'function') {
        onSwitchToAddressTab();
      }

      // Note: County field not present in current data structure
      // You may need to add county logic based on your API response
    }
  }, [onParcelSelected, onSwitchToAddressTab, onUpdateQuery]);

  const createModalContent = useCallback((results, title) => {
    return (
      <ParcelSearchResultsModal
        parcelSearchResults={results}
        onClose={closeModal}
        onSelect={handleParcelSelect}
        title={title}
      />
    );
  }, [closeModal, handleParcelSelect]);

  const openSearchModal = useCallback((results, title, size = "halffull") => {
    const modals = {
      searchResults: {
        title,
        content: createModalContent(results, title),
      }
    };
    openModal({ ...modals.searchResults, size });
  }, [createModalContent, openModal]);

  const handleSearchParcel = useCallback(async (parcelNumber, selectedCounty) => {
   // console.log('🔍 [useSearchHandlers] Parcel search initiated:', { parcelNumber, selectedCounty });

    if (!parcelNumber.trim() || !selectedCounty) {
   //   console.log('⚠️ [useSearchHandlers] Parcel search aborted - missing required data');
      return;
    }

    try {
   //   console.log('⏳ [useSearchHandlers] Starting parcel search...');
      setParcelState(prev => ({ ...prev, isSearching: true }));

      const payload = {
        parcel_number: parcelNumber.trim(),
        state_county_fips: selectedCounty.code
      };
   //   console.log('📡 [useSearchHandlers] Parcel search payload:', payload);

      const response = await api.post("/search-parcel-number", payload);
   //   console.log('📡 [useSearchHandlers] Parcel search response:', response);

      const results = response?.data?.status === "success" ? response.data.data : [];
   //   console.log('📋 [useSearchHandlers] Parcel search results:', results);

      openSearchModal(results, "Parcel Search Results");
   //   console.log('✅ [useSearchHandlers] Parcel search modal opened with results');

    } catch (error) {
  //    console.error("❌ [useSearchHandlers] Error searching parcel:", error);
      openSearchModal([], "Parcel Search Results");
    } finally {
      setParcelState(prev => ({ ...prev, isSearching: false }));
     // console.log('🏁 [useSearchHandlers] Parcel search completed');
    }
  }, [openSearchModal]);

  const handleSearchOwner = useCallback(async (firstName, lastName, selectedCounty, searchEntireState) => {
 //   console.log('🔍 [useSearchHandlers] Owner search initiated:', { firstName, lastName, selectedCounty, searchEntireState });

    if (!firstName.trim() && !lastName.trim()) {
  //    console.log('⚠️ [useSearchHandlers] Owner search aborted - missing name data');
      return;
    }

    try {
   //   console.log('⏳ [useSearchHandlers] Starting owner search...');
      setOwnerState(prev => ({ ...prev, isSearching: true }));

      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        state_county_fips: selectedCounty?.code || "",
        search_entire_state: searchEntireState
      };
    //  console.log('📡 [useSearchHandlers] Owner search payload:', payload);

      const response = await api.post("/search-owner-name", payload);
   //   console.log('📡 [useSearchHandlers] Owner search response:', response);

      const results = response?.data?.status === "success" ? response.data.data : [];
   //   console.log('📋 [useSearchHandlers] Owner search results:', results);

      openSearchModal(results, "Owner Search Results");
   //   console.log('✅ [useSearchHandlers] Owner search modal opened with results');

    } catch (error) {
    //  console.error("❌ [useSearchHandlers] Error searching owner:", error);
      openSearchModal([], "Owner Search Results");
    } finally {
      setOwnerState(prev => ({ ...prev, isSearching: false }));
    //  console.log('🏁 [useSearchHandlers] Owner search completed');
    }
  }, [openSearchModal]);

  const handleAddressSearch = useCallback(async (addressData) => {
 //   console.log('🔍 [useSearchHandlers] Address search initiated:', addressData);

    // Check if at least some address data is provided
    const hasData = Object.values(addressData).some(value =>
      typeof value === 'string' && value.trim().length > 0
    );

    if (!hasData) {
   //   console.log('⚠️ [useSearchHandlers] Address search aborted - no address data provided');
      return;
    }

    try {
   //   console.log('⏳ [useSearchHandlers] Starting address search...');
      setAddressState(prev => ({ ...prev, isSearching: true }));

      const payload = {
        street_no: addressData.street_no?.trim() || "",
        route: addressData.street_name?.trim() || "", // Short form
        route_long: addressData.street_name?.trim() || "",
        site_unit: addressData.unitNumber?.trim() || "",
        city: addressData.city?.trim() || "",
        state_code: addressData.state?.trim() || "",
        state_name: addressData.state?.trim() || "",
        zip: addressData.zip?.trim() || "",
        calc: mode,
      };
    //  console.log('📡 [useSearchHandlers] Address search payload:', payload);

      // Use the common API fetch function (same as autocomplete)
      const result = await fetchCityStateForZip(payload, { mode });
  //    console.log('📡 [useSearchHandlers] Address search result:', result);

      if (typeof onManualAddressResult === "function") {
        if (result.success) {
       //   console.log('✅ [useSearchHandlers] Address search success - full property details available');
          // API returned success - full property details available
          onManualAddressResult(null, result, payload);
        } else if (result.raw?.status === "fail" && result.raw?.data?.city) {
       //   console.log('⚠️ [useSearchHandlers] Address search partial success - address valid but no property details');
          // API returned fail but address data is valid - show address without property details
          onManualAddressResult(null, result, payload);
        } else {
       //   console.log('❌ [useSearchHandlers] Address search complete failure - address not found');
          // Complete failure - address not found or invalid
          onManualAddressResult(new Error(result.error), null, payload);
        }
      }
    } catch (error) {
  //    console.error("❌ [useSearchHandlers] Error searching address:", error);
      if (typeof onManualAddressResult === "function") {
        onManualAddressResult(error, null, null);
      }
    } finally {
      setAddressState(prev => ({ ...prev, isSearching: false }));
    }
  }, [onManualAddressResult, mode]);

  const updateParcelNumber = useCallback((value) => {
    setParcelState(prev => ({ ...prev, parcelNumber: value }));
  }, []);

  const updateOwnerField = useCallback((field, value) => {
    setOwnerState(prev => ({ ...prev, [field]: value }));
  }, []);

  return {
    // Parcel search
    parcelState,
    handleSearchParcel,
    updateParcelNumber,

    // Owner search
    ownerState,
    handleSearchOwner,
    updateOwnerField,

    // Address search
    addressState,
    handleAddressSearch,
  };
};